import React, {PropsWithChildren, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {useKeyPress} from '../../../utils/hooks/useKeyPress';
import {useOnClickOutside} from '../../../utils/hooks/useOnClickOutside';
import './confirmModal.scss';
import Button from '../../button/Button';
import {useDisableBodyScroll} from '../../../utils/hooks/useDisableBodyScroll';
import i18n from '../../../../i18n';

type ConfirmModalSize = 'lg' | 'md' | 'sm' | 'fit';

export interface ConfirmModalProps {
    isOpen: boolean;
    icon?: string;
    iconSize?: 'sm' | 'md' | 'lg';
    title?: string | JSX.Element;
    message?: string | JSX.Element;
    showCancel?: boolean;
    showConfirm?: boolean;
    cancelText?: string;
    confirmText?: string;
    onCancel: () => void;
    onConfirm?: () => void;
    size?: ConfirmModalSize;
    closeOnClickOutside?: boolean;
    color?: 'error' | 'success' | 'info' | 'warning';
    dataCy?: string;
}

function ConfirmModal(props: PropsWithChildren<ConfirmModalProps>): JSX.Element {
    const {
        isOpen,
        title = i18n.t('common.areYouSure'),
        message,
        showCancel = true,
        cancelText = i18n.t('common.cancel'),
        icon = 'solar:danger-triangle-bold',
        iconSize,
        showConfirm = true,
        confirmText = i18n.t('common.confirm'),
        onCancel,
        onConfirm,
        size = 'fit',
        closeOnClickOutside = true,
        color = 'info',
        dataCy,
        children,
    } = props;

    const {t} = useTranslation();
    const dropdownRef = useRef(null);
    const isEscPressed = useKeyPress('Escape');

    useEffect(() => {
        if (isEscPressed && isOpen) onCancel();
    }, [isEscPressed, onCancel, isOpen]);

    useOnClickOutside(dropdownRef, () => {
        if (isOpen && closeOnClickOutside) onCancel();
    });

    useDisableBodyScroll(isOpen);

    const iconSizeMap: Record<'sm' | 'md' | 'lg', number> = {sm: 32, md: 48, lg: 64};
    const iconPx = iconSize && iconSizeMap[iconSize];

    return (
        <div className={`c-confirmModal ${size ? `c-confirmModal--${size}` : ''} ${isOpen ? 'is-open' : ''}`} aria-hidden={!isOpen}>
            <div className="c-confirmModal__overlay" tabIndex={-1}>
                <div className="c-confirmModal__body" role="dialog" aria-modal="true" ref={dropdownRef} data-cy={dataCy}>
                    <div className="c-confirmModal__close">
                        <Button size="sm" variant="ghost" square onClick={onCancel} leading="ph:x-bold" />
                    </div>
                    <div className={`c-confirmModal__header c-confirmModal__header--${color}`}>
                        <Icon
                            className={`c-confirmModal__header_icon c-confirmModal__header_icon--${color}`}
                            icon={icon}
                            width={iconPx}
                            height={iconPx}
                        />
                    </div>
                    <div className="c-confirmModal__title">{title}</div>
                    <div className="c-confirmModal__message">{message}</div>

                    {children}

                    {(showCancel || showConfirm) && (
                        <div className="c-confirmModal__footer">
                            {showCancel && (
                                <Button
                                    type="button"
                                    className="c-confirmModal__button"
                                    fullWidth
                                    color="primary"
                                    variant="outline"
                                    size="md"
                                    onClick={onCancel}
                                >
                                    {cancelText || t('common.cancel')}
                                </Button>
                            )}
                            {showConfirm && (
                                <Button
                                    className="c-confirmModal__button"
                                    fullWidth
                                    color={color}
                                    variant="solid"
                                    size="md"
                                    onClick={onConfirm}
                                    type="button"
                                    dataCy="modal-confirm"
                                >
                                    {confirmText || t('common.confirm')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ConfirmModal;
